'use client';

import { browserClient } from '@app/sanity/lib/browser-client';
import { favouriteBoatsQuery } from '@app/sanity/lib/queries';
import type { FavouriteBoatsQueryResult } from '@app/sanity.types';
import { useAuth } from '@clerk/nextjs';
import { useFavoritesQuery } from '@marine/services';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type FavouriteBoat = FavouriteBoatsQueryResult[number];

/**
 * The same browser client with stega encoding off.
 *
 * `browserClient` encodes every string it returns — that is what makes fields clickable in the
 * Presentation tool — and the encoding is invisible characters appended to the value. `_id` is a
 * string like any other, so an encoded one no longer equals the plain `favoritable_ref` a favorite
 * stores: the lookup that pairs a boat with its favorite silently matched nothing and the page
 * rendered as if the account had saved none. Nothing here is visually editable, so the encoding
 * costs a working page and buys nothing.
 */
const boatsClient = browserClient.withConfig({ stega: false });

/**
 * Every boat the signed-in user has favorited, in the order they favorited them.
 *
 * Two reads, because the two halves live in different systems: the favorites are the user's own
 * data behind the Laravel API, and a favorite stores nothing but the boat's Sanity `_id`. Neither
 * can be done on the server — the favorites need the caller's Clerk session, and the ids they
 * return are not known until that request answers — so both run in the browser.
 *
 * The Sanity read is keyed on the ids sorted, not on the favorites list. Sorting keeps the key
 * stable when the API returns the same set in a different order, so re-ordering alone does not
 * refetch a page of boats that have not changed.
 */
export const useFavouriteBoats = () => {
  const { isLoaded, isSignedIn } = useAuth();

  const {
    data: favorites,
    isPending: isLoadingFavorites,
    isError: isFavoritesError,
    refetch: refetchFavorites,
  } = useFavoritesQuery({ enabled: !!isSignedIn });

  // The order the page renders in — the API returns favorites newest first.
  const orderedRefs = useMemo(() => (favorites ?? []).map((favorite) => favorite.favoritable_ref), [favorites]);

  const idsForQuery = useMemo(() => [...orderedRefs].sort(), [orderedRefs]);

  const {
    data: boats,
    isPending: isLoadingBoats,
    isError: isBoatsError,
    refetch: refetchBoats,
  } = useQuery({
    queryKey: ['favouriteBoats', idsForQuery],
    queryFn: () => boatsClient.fetch<FavouriteBoatsQueryResult>(favouriteBoatsQuery, { ids: idsForQuery }),
    enabled: !!isSignedIn && idsForQuery.length > 0,
    // Un-favoriting changes the id set, which changes this key. Holding the previous page of boats
    // while the new one loads is what stops the whole grid blanking out when one card is removed.
    placeholderData: keepPreviousData,
  });

  /**
   * Ordered by when they were favorited, and filtered back down to what is currently favorited.
   *
   * The filter is what makes un-favoriting feel immediate: the toggle writes the favorites cache
   * before its request answers, so the card leaves on the click rather than a round trip later —
   * and while `keepPreviousData` is still showing the boat this page no longer wants.
   */
  const favouriteBoats = useMemo(() => {
    if (!boats) {
      return [];
    }

    const boatById = new Map(boats.map((boat) => [boat._id, boat]));

    return orderedRefs.map((ref) => boatById.get(ref)).filter((boat): boat is FavouriteBoat => boat !== undefined);
  }, [boats, orderedRefs]);

  /**
   * The boats query is disabled while there are no favorites, and a disabled query is `isPending`
   * forever — so it cannot be read as loading on its own, or an empty list would spin indefinitely.
   */
  const isLoading = !isLoaded || (!!isSignedIn && (isLoadingFavorites || (orderedRefs.length > 0 && isLoadingBoats)));

  return {
    favouriteBoats,
    isLoading,
    isError: isFavoritesError || isBoatsError,
    /**
     * Favorites first: the ids it returns are what the Sanity read is keyed on, so retrying it
     * alone would refetch the same boats against the same possibly-stale id list.
     */
    retry: () => {
      refetchFavorites();
      refetchBoats();
    },
  };
};

export default useFavouriteBoats;
